// Realistic placeholder data for Tzaneen Mineral Project — values in ZAR (Rand)
// Conversion basis: ZAR 18.5 per USD, rounded to clean Rand figures.

export const fmtZar = (millions) => {
  if (millions >= 1000) return `R${(millions / 1000).toFixed(2)}bn`;
  return `R${Math.round(millions).toLocaleString()}m`;
};

export const mineMeta = {
  name: "Tzaneen Mineral Project",
  location: "Limpopo, South Africa",
  commodity: "Mixed (Coal · PGM · Chrome)",
  status: "Operational",
  established: 2008,
  workforce: 1240,
  coordinates: { lat: -23.83, lng: 30.16 },
};

export const tzaneenInfo = {
  district: "Mopani District Municipality",
  province: "Limpopo Province",
  distanceFromPolokwane: "~120 km east",
  geologicalSetting: "Eastern limb of the Bushveld Igneous Complex",
  elevation: "≈ 750 m above sea level",
  climate: "Subtropical highveld — 540 mm annual rainfall",
  populationCatchment: "≈ 250,000 in district (skilled labour pool)",
  nearestPorts: [
    { name: "Maputo (Mozambique)", distance: "480 km via N4 corridor" },
    { name: "Richards Bay (RSA)", distance: "700 km via N2/R618" },
  ],
  rail: "Transnet Tzaneen branch — 14 km to siding",
  power: "Eskom 132 kV grid connection · 35 MW peak demand",
  community: "Royalty agreement with Limpopo Community Trust (1.5% gross)",
};

export const kpiSummary = {
  annualProduction: { value: "1.24M", unit: "tons / yr", delta: "+6.8% YoY" },
  estimatedRevenue: { value: "R3.4bn", unit: "ZAR · 2024 actuals", delta: "+9.2% YoY" },
  equipmentAvailability: { value: "87.4%", unit: "uptime", delta: "+2.1pts" },
  reserveLife: { value: "18 yrs", unit: "remaining", delta: "Stable" },
};

export const productionTrend = [
  { month: "Jan", production: 92000, target: 95000 },
  { month: "Feb", production: 88000, target: 95000 },
  { month: "Mar", production: 101000, target: 100000 },
  { month: "Apr", production: 104000, target: 100000 },
  { month: "May", production: 110000, target: 105000 },
  { month: "Jun", production: 108000, target: 105000 },
  { month: "Jul", production: 112000, target: 108000 },
  { month: "Aug", production: 115000, target: 110000 },
  { month: "Sep", production: 109000, target: 110000 },
  { month: "Oct", production: 118000, target: 112000 },
  { month: "Nov", production: 121000, target: 115000 },
  { month: "Dec", production: 124000, target: 118000 },
];

// Quarterly revenue & EBITDA in millions ZAR
export const revenueTrend = [
  { quarter: "Q1 23", revenue: 705,  ebitda: 205 },
  { quarter: "Q2 23", revenue: 760,  ebitda: 240 },
  { quarter: "Q3 23", revenue: 720,  ebitda: 220 },
  { quarter: "Q4 23", revenue: 815,  ebitda: 280 },
  { quarter: "Q1 24", revenue: 835,  ebitda: 295 },
  { quarter: "Q2 24", revenue: 890,  ebitda: 335 },
  { quarter: "Q3 24", revenue: 870,  ebitda: 315 },
  { quarter: "Q4 24", revenue: 965,  ebitda: 390 },
];

export const equipmentHealth = [
  { name: "Healthy", value: 64, color: "#059669" },
  { name: "Monitor", value: 23, color: "#D97706" },
  { name: "Critical", value: 13, color: "#DC2626" },
];

export const keyHighlights = [
  "Production up 6.8% YoY, beating target by 4.2%",
  "Equipment uptime above industry avg (87% vs 82%)",
  "Mining rights renewed through 2042",
  "BBBEE Level 3 compliant — preferred procurement status",
  "Probable + Proven reserves grew 4.1% after 2024 drilling",
];

export const investmentSnapshot = {
  askingPrice: "R5.74bn",
  ebitdaMultiple: "5.0x",
  paybackPeriod: "5.0 yrs",
  irr: "22.8%",
};

// ----------------- OPERATIONS — DRILL-DOWN -----------------
export const monthlyOps = [
  { period: "Jan", production: 92000,  target: 95000,  uptime: 84.2, downtimeHrs: 114 },
  { period: "Feb", production: 88000,  target: 95000,  uptime: 81.6, downtimeHrs: 132 },
  { period: "Mar", production: 101000, target: 100000, uptime: 86.8, downtimeHrs: 95 },
  { period: "Apr", production: 104000, target: 100000, uptime: 87.4, downtimeHrs: 91 },
  { period: "May", production: 110000, target: 105000, uptime: 88.6, downtimeHrs: 82 },
  { period: "Jun", production: 108000, target: 105000, uptime: 87.9, downtimeHrs: 87 },
  { period: "Jul", production: 112000, target: 108000, uptime: 89.1, downtimeHrs: 78 },
  { period: "Aug", production: 115000, target: 110000, uptime: 88.4, downtimeHrs: 84 },
  { period: "Sep", production: 109000, target: 110000, uptime: 86.7, downtimeHrs: 96 },
  { period: "Oct", production: 118000, target: 112000, uptime: 90.2, downtimeHrs: 71 },
  { period: "Nov", production: 121000, target: 115000, uptime: 91.0, downtimeHrs: 65 },
  { period: "Dec", production: 124000, target: 118000, uptime: 91.4, downtimeHrs: 62 },
];

export const weeklyOps = {
  Jan: [
    { period: "W1", production: 22000, target: 23750, uptime: 82.4, downtimeHrs: 30 },
    { period: "W2", production: 23000, target: 23750, uptime: 84.5, downtimeHrs: 26 },
    { period: "W3", production: 24500, target: 23750, uptime: 86.1, downtimeHrs: 24 },
    { period: "W4", production: 22500, target: 23750, uptime: 83.7, downtimeHrs: 27 },
  ],
  Feb: [
    { period: "W1", production: 21000, target: 23750, uptime: 79.5, downtimeHrs: 35 },
    { period: "W2", production: 22000, target: 23750, uptime: 81.2, downtimeHrs: 32 },
    { period: "W3", production: 23000, target: 23750, uptime: 83.0, downtimeHrs: 29 },
    { period: "W4", production: 22000, target: 23750, uptime: 82.6, downtimeHrs: 30 },
  ],
  Mar: [
    { period: "W1", production: 25000, target: 25000, uptime: 86.8, downtimeHrs: 22 },
    { period: "W2", production: 26000, target: 25000, uptime: 88.1, downtimeHrs: 20 },
    { period: "W3", production: 24500, target: 25000, uptime: 85.7, downtimeHrs: 24 },
    { period: "W4", production: 25500, target: 25000, uptime: 86.4, downtimeHrs: 23 },
  ],
  Apr: [
    { period: "W1", production: 26500, target: 25000, uptime: 88.2, downtimeHrs: 20 },
    { period: "W2", production: 26000, target: 25000, uptime: 87.5, downtimeHrs: 21 },
    { period: "W3", production: 25500, target: 25000, uptime: 86.9, downtimeHrs: 22 },
    { period: "W4", production: 26000, target: 25000, uptime: 86.8, downtimeHrs: 22 },
  ],
  May: [
    { period: "W1", production: 27500, target: 26250, uptime: 89.1, downtimeHrs: 18 },
    { period: "W2", production: 28000, target: 26250, uptime: 89.6, downtimeHrs: 17 },
    { period: "W3", production: 27500, target: 26250, uptime: 88.4, downtimeHrs: 19 },
    { period: "W4", production: 27000, target: 26250, uptime: 87.2, downtimeHrs: 21 },
  ],
  Jun: [
    { period: "W1", production: 27000, target: 26250, uptime: 88.0, downtimeHrs: 20 },
    { period: "W2", production: 27500, target: 26250, uptime: 88.4, downtimeHrs: 19 },
    { period: "W3", production: 26500, target: 26250, uptime: 87.1, downtimeHrs: 22 },
    { period: "W4", production: 27000, target: 26250, uptime: 88.2, downtimeHrs: 20 },
  ],
  Jul: [
    { period: "W1", production: 28000, target: 27000, uptime: 89.4, downtimeHrs: 17 },
    { period: "W2", production: 28500, target: 27000, uptime: 90.0, downtimeHrs: 16 },
    { period: "W3", production: 28000, target: 27000, uptime: 89.0, downtimeHrs: 18 },
    { period: "W4", production: 27500, target: 27000, uptime: 88.0, downtimeHrs: 19 },
  ],
  Aug: [
    { period: "W1", production: 29000, target: 27500, uptime: 89.2, downtimeHrs: 18 },
    { period: "W2", production: 28500, target: 27500, uptime: 88.5, downtimeHrs: 19 },
    { period: "W3", production: 29000, target: 27500, uptime: 88.0, downtimeHrs: 20 },
    { period: "W4", production: 28500, target: 27500, uptime: 87.9, downtimeHrs: 20 },
  ],
  Sep: [
    { period: "W1", production: 27500, target: 27500, uptime: 86.5, downtimeHrs: 23 },
    { period: "W2", production: 27000, target: 27500, uptime: 86.0, downtimeHrs: 24 },
    { period: "W3", production: 27500, target: 27500, uptime: 87.2, downtimeHrs: 22 },
    { period: "W4", production: 27000, target: 27500, uptime: 87.1, downtimeHrs: 22 },
  ],
  Oct: [
    { period: "W1", production: 29500, target: 28000, uptime: 90.5, downtimeHrs: 16 },
    { period: "W2", production: 29000, target: 28000, uptime: 90.0, downtimeHrs: 17 },
    { period: "W3", production: 30000, target: 28000, uptime: 90.6, downtimeHrs: 16 },
    { period: "W4", production: 29500, target: 28000, uptime: 89.7, downtimeHrs: 18 },
  ],
  Nov: [
    { period: "W1", production: 30500, target: 28750, uptime: 91.4, downtimeHrs: 15 },
    { period: "W2", production: 30000, target: 28750, uptime: 91.0, downtimeHrs: 16 },
    { period: "W3", production: 30500, target: 28750, uptime: 90.8, downtimeHrs: 16 },
    { period: "W4", production: 30000, target: 28750, uptime: 90.6, downtimeHrs: 17 },
  ],
  Dec: [
    { period: "W1", production: 31000, target: 29500, uptime: 91.7, downtimeHrs: 14 },
    { period: "W2", production: 31500, target: 29500, uptime: 92.0, downtimeHrs: 13 },
    { period: "W3", production: 30500, target: 29500, uptime: 91.2, downtimeHrs: 15 },
    { period: "W4", production: 31000, target: 29500, uptime: 90.8, downtimeHrs: 16 },
  ],
};

const DAILY_TEMPLATE = [
  { day: "Mon", base: 1.02, uptimeAdj: 0.5 },
  { day: "Tue", base: 1.05, uptimeAdj: 0.8 },
  { day: "Wed", base: 1.04, uptimeAdj: 0.6 },
  { day: "Thu", base: 1.06, uptimeAdj: 1.0 },
  { day: "Fri", base: 1.00, uptimeAdj: 0.0 },
  { day: "Sat", base: 0.92, uptimeAdj: -1.2 },
  { day: "Sun", base: 0.91, uptimeAdj: -1.5 },
];

export function getDailyOps(weekData) {
  const dailyAvg = weekData.production / 7;
  return DAILY_TEMPLATE.map((d) => ({
    period: d.day,
    production: Math.round(dailyAvg * d.base),
    target: Math.round(weekData.target / 7),
    uptime: +(weekData.uptime + d.uptimeAdj).toFixed(1),
    downtimeHrs: Math.max(0, Math.round((weekData.downtimeHrs / 7) - d.uptimeAdj)),
  }));
}

// ----------------- ASSET REGISTER -----------------
export const equipmentList = [
  { id: "EQ-101", type: "Haul Truck (CAT 793F)", category: "Haulage", status: "Active", uptime: 91.2, lastService: "2025-01-12", nextService: "2025-04-12", commissioned: 2019, lifeKm: 184500, lifeHrs: 22300, kmSinceService: 4200, healthScore: 88 },
  { id: "EQ-102", type: "Haul Truck (CAT 793F)", category: "Haulage", status: "Active", uptime: 88.5, lastService: "2025-01-08", nextService: "2025-04-08", commissioned: 2019, lifeKm: 192300, lifeHrs: 23100, kmSinceService: 4800, healthScore: 84 },
  { id: "EQ-103", type: "Haul Truck (Komatsu 830E)", category: "Haulage", status: "Active", uptime: 86.7, lastService: "2025-01-15", nextService: "2025-04-15", commissioned: 2020, lifeKm: 156200, lifeHrs: 18900, kmSinceService: 3600, healthScore: 81 },
  { id: "EQ-104", type: "Articulated Dump Truck (Volvo A40G)", category: "Haulage", status: "Active", uptime: 89.4, lastService: "2025-01-19", nextService: "2025-04-19", commissioned: 2021, lifeKm: 124800, lifeHrs: 15400, kmSinceService: 2900, healthScore: 90 },
  { id: "EQ-201", type: "Hydraulic Excavator (CAT 6020B)", category: "Excavation", status: "Active", uptime: 84.1, lastService: "2024-12-20", nextService: "2025-03-20", commissioned: 2018, lifeKm: 0, lifeHrs: 27800, kmSinceService: 0, hrsSinceService: 720, healthScore: 76 },
  { id: "EQ-202", type: "Hydraulic Excavator (Liebherr R9200)", category: "Excavation", status: "Idle", uptime: 0, lastService: "2025-01-22", nextService: "2025-04-22", commissioned: 2022, lifeKm: 0, lifeHrs: 11200, kmSinceService: 0, hrsSinceService: 50, healthScore: 92 },
  { id: "EQ-203", type: "Wheel Loader (CAT 994K)", category: "Loading", status: "Active", uptime: 85.3, lastService: "2025-01-10", nextService: "2025-04-10", commissioned: 2020, lifeKm: 142400, lifeHrs: 17600, kmSinceService: 3800, healthScore: 80 },
  { id: "EQ-204", type: "Front-End Loader (Komatsu WA600)", category: "Loading", status: "Active", uptime: 88.0, lastService: "2025-01-17", nextService: "2025-04-17", commissioned: 2021, lifeKm: 118300, lifeHrs: 14600, kmSinceService: 3100, healthScore: 86 },
  { id: "EQ-301", type: "Blast-Hole Drill (Atlas Copco DM45)", category: "Drilling", status: "Active", uptime: 76.3, lastService: "2024-12-30", nextService: "2025-03-30", commissioned: 2017, lifeKm: 0, lifeHrs: 31200, kmSinceService: 0, hrsSinceService: 980, healthScore: 64 },
  { id: "EQ-302", type: "Blast-Hole Drill (Sandvik DR580)", category: "Drilling", status: "Maintenance", uptime: 0, lastService: "2025-01-25", nextService: "2025-04-25", commissioned: 2020, lifeKm: 0, lifeHrs: 19800, kmSinceService: 0, hrsSinceService: 20, healthScore: 58 },
  { id: "EQ-303", type: "Diamond Core Drill (Boart Longyear LF160)", category: "Drilling", status: "Active", uptime: 82.4, lastService: "2025-01-11", nextService: "2025-04-11", commissioned: 2022, lifeKm: 0, lifeHrs: 9800, kmSinceService: 0, hrsSinceService: 410, healthScore: 88 },
  { id: "EQ-401", type: "Primary Gyratory Crusher (Metso MK-III)", category: "Processing", status: "Active", uptime: 89.7, lastService: "2024-12-18", nextService: "2025-03-18", commissioned: 2015, lifeKm: 0, lifeHrs: 58400, kmSinceService: 0, hrsSinceService: 1240, healthScore: 72 },
  { id: "EQ-402", type: "Secondary Cone Crusher (Sandvik CH880)", category: "Processing", status: "Active", uptime: 92.1, lastService: "2025-01-06", nextService: "2025-04-06", commissioned: 2018, lifeKm: 0, lifeHrs: 36200, kmSinceService: 0, hrsSinceService: 580, healthScore: 84 },
  { id: "EQ-403", type: "Vibrating Screen (Schenck SLG3070)", category: "Processing", status: "Active", uptime: 94.3, lastService: "2025-01-14", nextService: "2025-04-14", commissioned: 2019, lifeKm: 0, lifeHrs: 31100, kmSinceService: 0, hrsSinceService: 410, healthScore: 91 },
  { id: "EQ-501", type: "Overland Conveyor System A (3.2 km)", category: "Conveying", status: "Active", uptime: 94.6, lastService: "2025-01-15", nextService: "2025-04-15", commissioned: 2014, lifeKm: 0, lifeHrs: 64800, kmSinceService: 0, hrsSinceService: 380, healthScore: 89 },
  { id: "EQ-502", type: "Overland Conveyor System B (1.8 km)", category: "Conveying", status: "Active", uptime: 92.0, lastService: "2025-01-04", nextService: "2025-04-04", commissioned: 2016, lifeKm: 0, lifeHrs: 52400, kmSinceService: 0, hrsSinceService: 660, healthScore: 87 },
  { id: "EQ-601", type: "Mobile Generator (CAT 3516)", category: "Power", status: "Active", uptime: 96.2, lastService: "2025-01-20", nextService: "2025-04-20", commissioned: 2021, lifeKm: 0, lifeHrs: 14200, kmSinceService: 0, hrsSinceService: 220, healthScore: 95 },
  { id: "EQ-602", type: "Substation Transformer (132/11 kV)", category: "Power", status: "Active", uptime: 99.1, lastService: "2024-11-10", nextService: "2025-05-10", commissioned: 2014, lifeKm: 0, lifeHrs: 78400, kmSinceService: 0, hrsSinceService: 1980, healthScore: 93 },
  { id: "EQ-701", type: "Water Bowser (40,000 L)", category: "Service", status: "Active", uptime: 90.5, lastService: "2025-01-21", nextService: "2025-04-21", commissioned: 2020, lifeKm: 89400, lifeHrs: 11200, kmSinceService: 1900, healthScore: 87 },
  { id: "EQ-702", type: "Grader (CAT 16M)", category: "Service", status: "Active", uptime: 87.7, lastService: "2025-01-09", nextService: "2025-04-09", commissioned: 2019, lifeKm: 78200, lifeHrs: 12800, kmSinceService: 2400, healthScore: 82 },
  { id: "EQ-703", type: "Dozer (CAT D11T)", category: "Service", status: "Active", uptime: 84.9, lastService: "2025-01-16", nextService: "2025-04-16", commissioned: 2018, lifeKm: 0, lifeHrs: 24600, kmSinceService: 0, hrsSinceService: 720, healthScore: 79 },
  { id: "EQ-704", type: "Light Vehicle Fleet (24 units)", category: "Service", status: "Active", uptime: 93.0, lastService: "2025-01-23", nextService: "2025-04-23", commissioned: 2022, lifeKm: 412000, lifeHrs: 0, kmSinceService: 8400, healthScore: 89 },
];

// Capex in millions ZAR (was $M, now ZAR)
export const equipmentByCategory = [
  { category: "Haulage", count: 4, avgUptime: 88.95, capex: 712 },
  { category: "Excavation", count: 2, avgUptime: 42.05, capex: 444 },
  { category: "Loading", count: 2, avgUptime: 86.65, capex: 230 },
  { category: "Drilling", count: 3, avgUptime: 52.9, capex: 181 },
  { category: "Processing", count: 3, avgUptime: 92.03, capex: 762 },
  { category: "Conveying", count: 2, avgUptime: 93.3, capex: 344 },
  { category: "Power", count: 2, avgUptime: 97.65, capex: 137 },
  { category: "Service", count: 4, avgUptime: 89.03, capex: 115 },
];

export const maintenanceTimeline = [
  { assetId: "EQ-101", asset: "Haul Truck CAT 793F", date: "2024-08-14", type: "planned", durationHrs: 18, note: "1000-hr inspection" },
  { assetId: "EQ-201", asset: "Excavator CAT 6020B", date: "2024-08-22", type: "unplanned", durationHrs: 36, note: "Hydraulic line failure" },
  { assetId: "EQ-401", asset: "Primary Crusher", date: "2024-09-05", type: "planned", durationHrs: 24, note: "Mantle inspection" },
  { assetId: "EQ-301", asset: "Drill Rig DM45", date: "2024-09-18", type: "unplanned", durationHrs: 72, note: "Compressor seizure" },
  { assetId: "EQ-501", asset: "Conveyor A", date: "2024-10-02", type: "planned", durationHrs: 12, note: "Belt splice replacement" },
  { assetId: "EQ-102", asset: "Haul Truck CAT 793F", date: "2024-10-15", type: "planned", durationHrs: 16, note: "Brake overhaul" },
  { assetId: "EQ-402", asset: "Cone Crusher CH880", date: "2024-10-28", type: "unplanned", durationHrs: 8, note: "Liner change" },
  { assetId: "EQ-203", asset: "Wheel Loader CAT 994K", date: "2024-11-10", type: "planned", durationHrs: 14, note: "Engine service" },
  { assetId: "EQ-301", asset: "Drill Rig DM45", date: "2024-11-25", type: "unplanned", durationHrs: 28, note: "Hammer rebuild" },
  { assetId: "EQ-401", asset: "Primary Crusher", date: "2024-12-09", type: "planned", durationHrs: 32, note: "Quarterly major service" },
  { assetId: "EQ-101", asset: "Haul Truck CAT 793F", date: "2025-01-12", type: "planned", durationHrs: 18, note: "Scheduled inspection" },
  { assetId: "EQ-302", asset: "Drill Rig DR580", date: "2025-01-25", type: "planned", durationHrs: 48, note: "Annual rebuild — in progress" },
];

// Operational bottlenecks — now with ZAR financial impact
export const bottlenecks = [
  { title: "Crusher throughput", impact: "High", note: "Primary crusher at 92% capacity — replacement liners scheduled", financialImpact: "R34m/month deferred revenue" },
  { title: "Haul cycle time", impact: "Medium", note: "Avg cycle 28 min vs 24 min target — pit access road realignment in progress", financialImpact: "R8.4m/month efficiency drag" },
  { title: "Skilled operator shortage", impact: "Medium", note: "5 vacant senior operator roles in shift B", financialImpact: "R2.8m/month uptime loss" },
];

// Geological
export const reserveDistribution = [
  { name: "Coal", value: 14_200_000, color: "#0F172A" },
  { name: "Platinum Group", value: 3_800_000, color: "#B45309" },
  { name: "Chrome", value: 2_400_000, color: "#475569" },
];

export const extractionTimeline = [
  { year: "2025", reserves: 22_400_000 },
  { year: "2027", reserves: 19_600_000 },
  { year: "2029", reserves: 17_200_000 },
  { year: "2031", reserves: 14_800_000 },
  { year: "2033", reserves: 12_300_000 },
  { year: "2035", reserves: 9_700_000 },
  { year: "2037", reserves: 7_100_000 },
  { year: "2039", reserves: 4_500_000 },
  { year: "2041", reserves: 1_900_000 },
];

export const resourceBreakdown = [
  { category: "Proven", coal: 9_400_000, pgm: 2_500_000, chrome: 1_600_000 },
  { category: "Probable", coal: 4_800_000, pgm: 1_300_000, chrome: 800_000 },
];

// Financial — share of OpEx (% unchanged)
export const costBreakdown = [
  { name: "Labour", value: 32, color: "#0F172A" },
  { name: "Energy & Fuel", value: 24, color: "#B45309" },
  { name: "Maintenance", value: 18, color: "#475569" },
  { name: "Royalties & Tax", value: 14, color: "#1E40AF" },
  { name: "Logistics", value: 8, color: "#059669" },
  { name: "Other", value: 4, color: "#94A3B8" },
];

// Profitability in millions ZAR
export const profitability = [
  { year: "2020", revenue: 2295, opex: 1775, ebitda: 520 },
  { year: "2021", revenue: 2555, opex: 1890, ebitda: 665 },
  { year: "2022", revenue: 2815, opex: 2035, ebitda: 780 },
  { year: "2023", revenue: 3110, opex: 2185, ebitda: 925 },
  { year: "2024", revenue: 3415, opex: 2260, ebitda: 1155 },
];

export const costDrivers = [
  { label: "Diesel price exposure", value: "R39m sensitivity per R2/L" },
  { label: "Power tariff hike", value: "Eskom +18% YoY pressure" },
  { label: "Labour escalation", value: "CPI + 1.5% annual band" },
];

// Client breakdown — revenue in millions ZAR
export const clientBreakdown = [
  { name: "Eskom (Power utility)", revenue: 1300, share: 38, contract: "Long-term · 7 yrs", commodity: "Coal", color: "#0F172A" },
  { name: "ArcelorMittal SA", revenue: 750,  share: 22, contract: "Off-take · 3 yrs", commodity: "Coal · Chrome", color: "#B45309" },
  { name: "Glencore Trading", revenue: 545,  share: 16, contract: "Spot + Term", commodity: "PGM concentrate", color: "#475569" },
  { name: "Anglo American Refining", revenue: 410, share: 12, contract: "Off-take · 5 yrs", commodity: "PGM", color: "#1E40AF" },
  { name: "Sasol Industrial", revenue: 275,  share: 8,  contract: "Term · 2 yrs", commodity: "Coal", color: "#059669" },
  { name: "Spot Market / Other", revenue: 135, share: 4,  contract: "Spot", commodity: "Mixed", color: "#94A3B8" },
];

// Legal & Compliance
export const compliance = [
  { area: "Mining Right (DMRE)", status: "green", detail: "Valid until 2042-06-30" },
  { area: "Water Use License", status: "green", detail: "Valid until 2031-04-12" },
  { area: "Environmental Authorization", status: "amber", detail: "Renewal in progress (2025-Q3)" },
  { area: "Air Emissions License", status: "green", detail: "Valid until 2028-09-01" },
  { area: "BBBEE Level", status: "green", detail: "Level 3 — Verified 2024-11" },
  { area: "Surface Lease Agreement", status: "amber", detail: "Renegotiation pending — 2 of 4 plots" },
  { area: "Section 11 Approvals", status: "green", detail: "All transfers approved" },
  { area: "Litigation", status: "green", detail: "No active litigation" },
];

export const ownership = [
  { holder: "Tzaneen Holdings Pty Ltd", pct: 51 },
  { holder: "Limpopo Community Trust", pct: 26 },
  { holder: "Strategic Investor Co.", pct: 15 },
  { holder: "Employee Share Scheme", pct: 8 },
];

// Risk & Insights
export const riskMatrix = [
  { category: "Operational", level: "amber", score: 6.2, note: "Crusher capacity nearing limit" },
  { category: "Financial", level: "green", score: 3.4, note: "Strong EBITDA margin, low debt" },
  { category: "Legal", level: "green", score: 2.8, note: "Clean title, valid licenses" },
  { category: "Environmental", level: "amber", score: 5.5, note: "EA renewal in progress" },
];

export const keyRisks = [
  "Eskom power instability could disrupt 8–12 days of production annually",
  "Environmental Authorization renewal pending — expected by Q3 2025",
  "Crusher capacity bottleneck if production scales above 1.4M tons/yr",
];

export const valueOpportunities = [
  "Debottleneck crusher → +12% throughput potential (R407m revenue uplift)",
  "Solar PPA could reduce energy cost by 18% (R85m/yr saving)",
  "Probable reserves upgrade after 2025 drilling could extend life by 4 yrs",
];

export const upsideSummary = {
  baseCase: "R5.74bn",
  upsideCase: "R7.12bn",
  rationale: "Crusher upgrade + solar PPA + reserves upgrade",
};

export const folders = ["Operations", "Financials", "Legal", "Geological", "Environmental"];
